import { ComponentType } from '@customTypes/cloudmix/sessions/components/base'

/**
 * Discriminated union for feed ownership.
 *
 * - 'project' refs are durable: they survive session restarts.
 * - 'session' refs are ephemeral: they exist only while the session is live.
 */
export type FeedRef =
  | {
      scope: 'project'
      projectId: string
      componentId: string
      padName: string
    }
  | {
      scope: 'session'
      sessionId: string
      /** Present if the session's parentResource is a project */
      projectId?: string
      componentId: string
      padName: string
    }

/** Persisted alongside the ref so expired tiles remain identifiable. */
export interface TileSnapshot {
  displayName: string
  componentType: ComponentType | null
}

/**
 * A tile in the multiview grid.
 * tileId is the layout identity — separate from the feed ref.
 * This allows the same feed to appear more than once.
 */
export interface Tile {
  tileId: string
  ref: FeedRef
  snapshot: TileSnapshot
}

/**
 * Fully-hydrated description of a feed's owner. Scope is decided here, once, at
 * the owner level — never per-feed at a call site. This is the sole input to
 * {@link buildFeedRef}, which is the only sanctioned way to construct a FeedRef.
 */
export type FeedOwner =
  | { kind: 'project'; projectId: string }
  | { kind: 'session'; sessionId: string; projectId?: string }

/**
 * Canonical FeedRef constructor. Every construction site must go through this so
 * scope and the optional session `projectId` are set consistently, rather than
 * each site reimplementing the project-vs-session decision.
 */
export const buildFeedRef = (
  owner: FeedOwner,
  componentId: string,
  padName: string
): FeedRef => {
  if (owner.kind === 'project') {
    return {
      scope: 'project',
      projectId: owner.projectId,
      componentId,
      padName,
    }
  }
  if (owner.projectId) {
    return {
      scope: 'session',
      sessionId: owner.sessionId,
      projectId: owner.projectId,
      componentId,
      padName,
    }
  }
  return { scope: 'session', sessionId: owner.sessionId, componentId, padName }
}

export const getOwnerKey = (ref: FeedRef): string =>
  ref.scope === 'project' ? `p:${ref.projectId}` : `s:${ref.sessionId}`

export const getStreamId = (ref: FeedRef): string =>
  `${ref.componentId}.${ref.padName}`

export const parseOwnerKey = (
  ownerKey: string
):
  | { scope: 'project'; projectId: string }
  | { scope: 'session'; sessionId: string } => {
  if (ownerKey.startsWith('p:')) {
    return { scope: 'project', projectId: ownerKey.slice(2) }
  }
  return { scope: 'session', sessionId: ownerKey.slice(2) }
}

export const feedRefEquals = (a: FeedRef, b: FeedRef): boolean => {
  if (a.scope !== b.scope) return false
  if (a.scope === 'project' && b.scope === 'project') {
    return (
      a.projectId === b.projectId &&
      a.componentId === b.componentId &&
      a.padName === b.padName
    )
  }
  if (a.scope === 'session' && b.scope === 'session') {
    return (
      a.sessionId === b.sessionId &&
      (a.projectId ?? null) === (b.projectId ?? null) &&
      a.componentId === b.componentId &&
      a.padName === b.padName
    )
  }
  return false
}
